import { MIN_MAP_ZOOM } from 'features/map/utils/map_constants';

type Frc =
  | 'motorway'
  | 'trunk'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'road'
  | 'unclassified'
  | 'residential'
  | 'living_street';

export const FRC_LABELS: Record<number, string> = {
  0: 'Motorway',
  1: 'Trunk',
  2: 'Primary',
  3: 'Secondary',
  4: 'Tertiary',
  5: 'Road',
  6: 'Residential',
} as const;

export const RESIDENTIAL_FRC = 6;

const FRC_IDS = [0, 1, 2, 3, 4, 5, 6] as const;
type FrcId = (typeof FRC_IDS)[number];

export const LINK_FILTER_SUFFIX = 'L';
const LINK_SUFFIX = '_link';
const DEFAULT_HWC: FrcId = RESIDENTIAL_FRC;

export const HIGHWAY_TO_FRC_ID: Record<Frc, FrcId> = {
  motorway: 0,
  trunk: 1,
  primary: 2,
  secondary: 3,
  tertiary: 4,
  road: 5,
  unclassified: 6,
  residential: 6,
  living_street: 6,
} as const;

/** Mirrors the server-side frc_at_zoom() */
export function frcAtZoom(zoom: number, disableResidential?: boolean): number {
  let frc: number;
  if (zoom < MIN_MAP_ZOOM) frc = null;
  else if (zoom < 10)
    frc = 0; // Motorways
  else if (zoom < 11)
    frc = 1; // Motorways and trunks
  else if (zoom < 12)
    frc = 2; //Primary and above
  else if (zoom < 14)
    frc = 3; //Secondary and above
  else if (zoom < 16)
    frc = 4; //Tertiary and above
  else frc = 10; //Residential and above
  if (zoom > MIN_MAP_ZOOM && disableResidential) frc = Math.min(frc, 5);
  return frc;
}

export const FRC_LINK_IDS = FRC_IDS.filter((el) => el !== RESIDENTIAL_FRC).map(
  (id) => toRoadClassFilterId(id, true),
);

const DEFAULT_ROAD_CLASSES = new Set<string>([
  ...FRC_IDS.map(String),
  ...FRC_LINK_IDS,
]);

const isLink = (s: string): boolean => s.endsWith(LINK_SUFFIX);

export function toRoadClassFilterId(
  frcId: string | number,
  isLinkFilter = false,
): string {
  return `${frcId}${isLinkFilter ? LINK_FILTER_SUFFIX : ''}`;
}

export function isLinkRoadClassFilterId(filterId: string): boolean {
  return filterId.endsWith(LINK_FILTER_SUFFIX);
}

function parseRoadClassFilterId(filterId: string): {
  frcId: string;
  isLink: boolean;
} {
  const isLink = isLinkRoadClassFilterId(filterId);
  return {
    frcId: isLink ? filterId.slice(0, -LINK_FILTER_SUFFIX.length) : filterId,
    isLink,
  };
}

function getRoadClassFromFilterId(filterId: string): string {
  return parseRoadClassFilterId(filterId).frcId;
}

function roadClassToFilterId(raw: string): string {
  const s = raw.trim().toLowerCase();
  if (!s) return toRoadClassFilterId(DEFAULT_HWC);
  const link = isLink(s);
  const base = link ? s.slice(0, -LINK_SUFFIX.length) : s;
  const frcId = HIGHWAY_TO_FRC_ID[base] ?? DEFAULT_HWC;
  return toRoadClassFilterId(frcId, link);
}

export function filterIdToRoadClass(filterId: string): string {
  const isLinkFilter = isLinkRoadClassFilterId(filterId);
  const baseId = getRoadClassFromFilterId(filterId);

  return `${FRC_LABELS[baseId]?.toLowerCase()}${isLinkFilter ? LINK_SUFFIX : ''}`;
}

export function allowedRoadClassFilterIdsFromHighwayTypes(
  highwayTypes?: readonly string[] | null,
): Set<string> {
  // db not yet updated
  if (!highwayTypes) return new Set(DEFAULT_ROAD_CLASSES);

  const out = new Set<string>();
  for (const raw of highwayTypes) out.add(roadClassToFilterId(raw));
  return out;
}

export function filterKeysBaseline(allowedIds: Set<string>): string[] {
  const filtered = [...DEFAULT_ROAD_CLASSES].filter((id) => allowedIds.has(id));
  return filtered.length > 0 ? filtered : [...DEFAULT_ROAD_CLASSES];
}
